import {StyledTargetDeletionNameText} from '@/components/commons/delete-dialog/style'
import {ConfirmationDialog} from '@/components/ui/confirmation-dialog-atoms/ConfirmationDialogAtoms'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Text} from '@/components/ui/text/Text'
import {EarlyClosingProjectConfirmationFormSchema} from '@/features/projects/formSchemas'
import {useEditProjectDetails} from '@/features/projects/queries/useEditProjectDetails'
import {ProjectExtended} from '@/features/projects/types'
import {QUERY_KEYS, queryClient} from '@/queryClient'
import {useTranslation} from '@/translations/i18n'
import {routes} from '@/utilities/constants/routes'
import {toast} from '@/utilities/toast/toast'
import {DialogStore} from '@ariakit/react'
import dayjs from 'dayjs'
import {FormProvider, useForm} from 'react-hook-form'
import {generatePath, useNavigate} from 'react-router-dom'
import {zodResolver} from '@hookform/resolvers/zod'
import {DialogHeading} from '@/components/ui/dialog-atoms/DialogAtoms'

export const EarlyClosingProjectConfirmationModal = ({
    dialogStore,
    defaultValues,
    project
}: {
    dialogStore: DialogStore
    defaultValues: EarlyClosingProjectConfirmationFormSchema
    project: ProjectExtended
}) => {
    const {t} = useTranslation()
    const form = useForm<EarlyClosingProjectConfirmationFormSchema>({
        resolver: zodResolver(EarlyClosingProjectConfirmationFormSchema),
        defaultValues
    })
    const navigate = useNavigate()
    const {mutate: editProjectDetails, isPending} = useEditProjectDetails({
        urlParams: {projectId: project.id},
        options: {
            onSuccess: async () => {
                dialogStore.setOpen(false)
                void queryClient.cancelQueries({queryKey: [QUERY_KEYS.PROJECTS]})
                void queryClient.cancelQueries({queryKey: [QUERY_KEYS.PROJECT, project.id]})
                void queryClient.invalidateQueries({queryKey: [QUERY_KEYS.AGENDA_EVENTS]})
                void queryClient.invalidateQueries({queryKey: [QUERY_KEYS.USER_AVAILABILITY]})
                await queryClient.invalidateQueries({queryKey: [QUERY_KEYS.PROJECTS]})
                await queryClient.invalidateQueries({queryKey: [QUERY_KEYS.PROJECT, project.id]})
                void queryClient.invalidateQueries({
                    queryKey: [QUERY_KEYS.TREATMENT_REQUESTS, project.treatmentRequestId]
                })
                void queryClient.invalidateQueries({queryKey: [QUERY_KEYS.PATIENTS, project.patientId]})
                toast.success(t('projects:early_closing_project_success', {code: project.code}))
                // The early closing acts on this same project: go back to its detail page, not to the list.
                navigate(generatePath(routes.PROJECT_DETAILS_PROJECT_DATA.handle.path, {id: project.id}), {
                    replace: true
                })
            },
            onError: () => {
                toast.error(t('errors:default'))
            }
        }
    })

    const handleConfirmSubmit = form.handleSubmit(
        data => {
            editProjectDetails({
                endDate: dayjs(data.endDate).format('YYYY-MM-DD'),
                conclusionReason: data.hasReopening ? undefined : data.endingReason,
                notificationRecipientIds: data.notificationsRecipients?.map(recipient => recipient.id),
                notificationDetails: data.notificationDetails
            })
        },
        error => {
            console.error(error)
        }
    )

    return (
        <FormProvider {...form}>
            <ConfirmationDialog
                dialogStore={dialogStore}
                onConfirm={handleConfirmSubmit}
                isConfirmPending={isPending}
                headerSlot={
                    <Flexbox direction="column">
                        <DialogHeading>{t('projects:project_early_closing_title')}</DialogHeading>
                        <Flexbox direction="column" gap={1}>
                            <Text palette="neutral" shade="500" size="textSm">
                                {t('projects:early_closing_project_confirmation')}
                            </Text>
                            <StyledTargetDeletionNameText palette="neutral" shade="500" size="textSm">
                                {t('projects:code_date', {
                                    code: project.code,
                                    date: dayjs(project.startDate).format('DD/MM/YYYY')
                                })}
                            </StyledTargetDeletionNameText>
                        </Flexbox>
                    </Flexbox>
                }
            />
        </FormProvider>
    )
}
